/**
 * One clinic patient: their details, assigned practitioner, lab uploads (PRA-011), and removal
 * (PRA-014).
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import type { ClinicPatientDetail, ClinicPatientList } from "@lattice/core";
import { LAB_FILE_ACCEPT, MAX_LAB_FILE_BYTES } from "@lattice/core";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { ConfirmDialog } from "@lattice/ui/ConfirmDialog";
import { DescriptionList } from "@lattice/ui/DescriptionList";
import { EmptyState } from "@lattice/ui/EmptyState";
import { Field } from "@lattice/ui/Field";
import { focusRing } from "@lattice/ui/focus";
import type { FileRow } from "@lattice/ui/FileDrop";
import { FileDrop, formatBytes } from "@lattice/ui/FileDrop";
import { Pending } from "@lattice/ui/Pending";
import { Section } from "@lattice/ui/Section";
import { Select } from "@lattice/ui/Select";
import { Text } from "@lattice/ui/Text";
import { loadClinicPatient, removeClinicPatient, retryClinicPatientDocument, setClinicPatientPractitioner, uploadClinicPatientFile } from "../api.ts";
import { navigate } from "../router.ts";
import { NotOpened } from "./NotOpened.tsx";
import { rememberRemoved } from "./removed.ts";
import { RecordList, RecordRow } from "./Record.tsx";
import { ClinicShell } from "./Shell.tsx";
const DOC_STATUS: Readonly<Record<ClinicPatientDetail["documents"][number]["status"], string>> = {
  stored: "Stored, waiting to be read",
  extracting: "Being read now",
  extracted: "Finished reading. The results are in the formula draft.",
  failed: "Could not be read. If it fails again, upload a clearer copy."
};
const DOC_REFRESH_MS = 5000;

/** Dates are UTC so the clinic and the server agree on the day a file was stored. */
function storedOn(createdAt: string): string {
  return new Date(createdAt.includes("T") ? createdAt : `${createdAt.replace(" ", "T")}Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC"
  });
}
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "missing";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "ready";
  readonly patient: ClinicPatientDetail;
};
function titleOf(patient: ClinicPatientDetail): string {
  const name = [patient.firstName, patient.lastName].filter(part => part !== null).join(" ");
  return name === "" ? "Patient" : name;
}

/** "formula_in_review" as "Formula in review", the way the patient list words it. */
function sentence(status: string): string {
  const words = status.replaceAll("_", " ");
  return `${words.slice(0, 1).toUpperCase()}${words.slice(1)}`;
}

/** "1990-01-15" as "January 15, 1990". An unreadable value is shown as it came. */
function longDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? iso : new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeZone: "UTC"
  }).format(date);
}
function PatientFacts({
  patient
}: {
  readonly patient: ClinicPatientDetail;
}) {
  return <DescriptionList variant="columns" items={[{
    term: "Email",
    value: patient.email ?? ""
  }, {
    term: "Born",
    value: patient.dateOfBirth === null ? "" : longDate(patient.dateOfBirth)
  }, {
    term: "Link",
    value: patient.status === "pending" ? "Waiting for them to accept this clinic's request." : "Linked to this clinic."
  }, {
    term: "Sharing",
    value: patient.shareScope === "all" ? "They shared all of their records with this clinic." : "This clinic can see records it created."
  }, {
    term: "Formula",
    content: <span data-testid="patient-formula-status">{sentence(patient.formulaStatus)}</span>
  }, {
    term: "Review",
    content: <span data-testid="patient-review-status">{sentence(patient.reviewStatus)}</span>
  }, {
    term: "Order",
    content: <span data-testid="patient-order-status">{sentence(patient.orderStatus)}</span>
  }]} />;
}
interface DocumentRetry {
  readonly retrying: string | null;
  readonly failed: string | null;
  /** A retry that was queued but whose reload failed, so the row still says it failed. */
  readonly queuedAgain: string | null;
  readonly onRetry: (documentId: string) => void;
}
function DocumentStatus({
  doc,
  retry
}: {
  readonly doc: ClinicPatientDetail["documents"][number];
  readonly retry: DocumentRetry;
}) {
  const retrying = retry.retrying === doc.id;
  const queued = retry.queuedAgain === doc.id;
  const words = retrying ? "Starting to read it again" : queued ? "Queued to be read again. This updates by itself." : DOC_STATUS[doc.status];
  return <>
      {/* Focus lands here once Read again leaves the row, so the new state is read out. */}
      <span role="status" id={`doc-status-${doc.id}`} tabIndex={-1} {...stylex.props(focusRing)}>
        <Text as="span" variant="caption" tone={doc.status === "failed" && !retrying && !queued ? "danger" : "secondary"}>
          {words}
        </Text>
      </span>
      {doc.status === "failed" && !queued ? <Button size="small" tone="quiet" corners="rounded" testId={`retry-${doc.id}`} ariaLabel={`Read again, ${doc.filename}`} loading={retrying} onClick={() => retry.onRetry(doc.id)}>
          Read again
        </Button> : null}
    </>;
}
function Documents({
  patient,
  uploads,
  error,
  retry,
  onFiles,
  onRemove
}: {
  readonly patient: ClinicPatientDetail;
  readonly uploads: readonly FileRow[];
  readonly error: string | null;
  readonly retry: DocumentRetry;
  readonly onFiles: (files: readonly File[]) => void;
  readonly onRemove: (id: string) => void;
}) {
  return <Section title="Documents" count={String(patient.documents.length)}>
      <Text tone="secondary">
        Labs, GI mapping, nutritional biomarker reports, and intake notes. They are read the same
        way as a customer's upload.
      </Text>
      <FileDrop label="Patient documents" extensions={LAB_FILE_ACCEPT.split(",").filter(one => one.startsWith("."))} maxBytes={MAX_LAB_FILE_BYTES} rows={uploads} onRemove={onRemove} onFiles={onFiles} testId="clinic-upload" />
      {error === null ? null : <Banner tone="danger" title="Not saved">
          {error}
        </Banner>}
      {patient.documents.length === 0 ? <EmptyState title="No documents yet." body="Drop a file above to add one." /> : <>
          <Text variant="label" tone="secondary">
            {patient.documents.length === 1 ? "1 document" : `${String(patient.documents.length)} documents, oldest first`}
          </Text>
          <RecordList label="Documents">
            {patient.documents.map(doc => <RecordRow key={doc.id} title={doc.filename} detail={`${formatBytes(doc.bytes)}, added ${storedOn(doc.createdAt)}`} trailing={<DocumentStatus doc={doc} retry={retry} />}>
                {retry.failed === doc.id ? <Banner tone="danger" title="Not started">
                    We could not start reading it again. Try again, or upload a clearer copy.
                  </Banner> : null}
              </RecordRow>)}
          </RecordList>
        </>}
    </Section>;
}
export function ClinicPatientPage({
  patientId,
  clinic
}: {
  readonly patientId: string;
  readonly clinic: ClinicPatientList;
}) {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [error, setError] = useState<string | null>(null);
  const [practitionerError, setPractitionerError] = useState<string | null>(null);
  const [uploads, setUploads] = useState<readonly FileRow[]>([]);
  const [removing, setRemoving] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryFailed, setRetryFailed] = useState<string | null>(null);
  const [queuedAgain, setQueuedAgain] = useState<string | null>(null);
  const [focusStatusOf, setFocusStatusOf] = useState<string | null>(null);
  /* Each look at the server takes a number, and only the newest one's answer is shown, so a slow poll cannot put back a list an upload has since changed. */
  const lastLook = useRef(0);

  /* The Read again button leaves the row once the file is queued, so focus moves to the row's status. */
  useEffect(() => {
    if (focusStatusOf === null) return;
    document.getElementById(`doc-status-${focusStatusOf}`)?.focus();
    setFocusStatusOf(null);
  }, [focusStatusOf]);

  /* A file that is stored or being read changes on the server without the page asking, so look again every few seconds until none is waiting. */
  const waiting = screen.kind === "ready" && (queuedAgain !== null || screen.patient.documents.some(doc => doc.status === "stored" || doc.status === "extracting"));
  useEffect(() => {
    if (!waiting) return;
    let live = true;
    const timer = setInterval(() => {
      lastLook.current += 1;
      const look = lastLook.current;
      void loadClinicPatient(patientId).then(loaded => {
        if (!live || look !== lastLook.current || loaded.status !== "ok") return;
        setScreen({
          kind: "ready",
          patient: loaded.patient
        });
        setQueuedAgain(id => loaded.patient.documents.some(doc => doc.id === id && doc.status !== "failed") ? null : id);
      }, () => undefined);
    }, DOC_REFRESH_MS);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [waiting, patientId]);
  useEffect(() => {
    void loadClinicPatient(patientId).then(loaded => {
      if (loaded.status !== "ok") setScreen({
        kind: "missing"
      });else setScreen({
        kind: "ready",
        patient: loaded.patient
      });
    }, () => setScreen({
      kind: "failed"
    }));
  }, [patientId]);
  if (screen.kind === "loading") return <ClinicShell title="Patient" signedIn clinicName={clinic.organizationName}>
        <Pending message="Loading the patient" bones={["title", "line", "line"]} />
      </ClinicShell>;
  if (screen.kind === "failed" || screen.kind === "missing") return <NotOpened title={screen.kind === "failed" ? "We couldn't load the patient" : "We couldn't open that patient"} body={screen.kind === "failed" ? "Try again in a minute. If it keeps happening, write to support." : "They may not be linked to this clinic."} retry={screen.kind === "failed"} back={{
    label: "Back to patients",
    href: "/clinic/patients"
  }} />;
  const {
    patient
  } = screen;

  /** The file or the change is already saved, so a reload that cannot reach Lattice keeps the page as it was. */
  async function reload() {
    lastLook.current += 1;
    const look = lastLook.current;
    const loaded = await loadClinicPatient(patientId).catch(() => null);
    if (loaded?.status !== "ok") return false;
    if (look !== lastLook.current) return true;
    setScreen({
      kind: "ready",
      patient: loaded.patient
    });
    return true;
  }
  async function retry(documentId: string) {
    setRetryFailed(null);
    setRetrying(documentId);
    if (await retryClinicPatientDocument(patientId, documentId).catch(() => false)) {
      if (!(await reload())) setQueuedAgain(documentId);
      setFocusStatusOf(documentId);
    } else setRetryFailed(documentId);
    setRetrying(null);
  }

  /* Each file is a row while it goes up. A stored file moves to the documents list below. */
  async function onFiles(files: readonly File[]) {
    setError(null);
    for (const file of files) {
      const id = `${file.name}-${String(file.size)}-${String(Date.now())}`;
      const row = {
        id,
        name: file.name,
        bytes: file.size
      } as const;
      setUploads(all => [...all, {
        ...row,
        state: "uploading"
      }]);
      const outcome = await uploadClinicPatientFile(patientId, file).catch(() => null);
      if (outcome === null || outcome.status === "rejected") {
        const message = outcome === null ? "We couldn't reach Lattice. Try again." : "We couldn't store that file. Use a PDF or a photo under 25 MB.";
        setUploads(all => all.map(one => one.id === id ? {
          ...row,
          state: "failed",
          message
        } : one));
        continue;
      }
      setUploads(all => all.filter(one => one.id !== id));
      await reload();
    }
  }
  async function assign(practitionerUserId: string) {
    setPractitionerError(null);
    const result = await setClinicPatientPractitioner(patientId, practitionerUserId).catch(() => false);
    if (!result) {
      setPractitionerError("We couldn't change the practitioner. Try again.");
      return;
    }
    await reload();
  }

  /* The dialog shows a failure itself and stays open, so the clinic can try again. */
  async function remove(): Promise<string | null> {
    if (!(await removeClinicPatient(patientId).catch(() => false))) return "We couldn't remove this patient. Try again.";
    rememberRemoved(titleOf(patient));
    navigate("/clinic", {
      force: true
    });
    return null;
  }
  const name = titleOf(patient);
  return <ClinicShell title={name} crumbs={[{
    label: "Patients",
    href: "/clinic/patients"
  }]} signedIn clinicName={clinic.organizationName}>
      <PatientFacts patient={patient} />
      <Section title="Practitioner">
        {clinic.roles.includes("owner") ? <Field label="Responsible practitioner" id="assign-practitioner" error={practitionerError}>
            {control => <Select {...control} options={[{
          value: "",
          label: "None"
        }, ...clinic.practitioners.map(member => ({
          value: member.userId,
          label: member.name || member.email
        }))]} value={patient.practitionerUserId ?? ""} onChange={value => void assign(value)} />}
          </Field> : <Text tone="secondary">{patient.practitionerName ?? "No practitioner assigned"}</Text>}
      </Section>
      {patient.status === "active" && clinic.roles.includes("practitioner") ? <Section title="Formula">
          <Text tone="secondary">
            Generate a draft from this patient's data, edit it, and submit it for a Lattice
            physician.
          </Text>
          <div>
            <Button testId="open-formula" corners="rounded" onClick={() => navigate(`/clinic/patients/${patientId}/formula`)}>
              Open formula
            </Button>
          </div>
        </Section> : null}
      {patient.status === "active" ? <Documents patient={patient} uploads={uploads} error={error} retry={{
      retrying,
      failed: retryFailed,
      queuedAgain,
      onRetry: id => void retry(id)
    }} onFiles={files => void onFiles(files)} onRemove={id => setUploads(all => all.filter(one => one.id !== id))} /> : null}
      {clinic.roles.includes("owner") || clinic.roles.includes("practitioner") ? <Section title="Remove from this clinic">
          <Text tone="secondary">
            This clinic loses access. The patient's records and subscription stay.
          </Text>
          <div>
            <Button variant="dangerSecondary" corners="rounded" testId="remove-patient" onClick={() => setRemoving(true)}>
              Remove patient
            </Button>
          </div>
        </Section> : null}
      <ConfirmDialog open={removing} onClose={() => setRemoving(false)} title={`Remove ${name} from this clinic?`} tone="danger" confirmLabel="Remove patient" cancelLabel="Keep patient" testId="remove-patient-dialog" onConfirm={remove}>
        This clinic loses access to their records and formula. The patient keeps both, and their
        subscription is not touched.
      </ConfirmDialog>
    </ClinicShell>;
}